// V3 shell — "Bento" card-first layout.
//
//   ┌──┬──────────────────────────────────────────┐
//   │ ◆│ Dashboard · breadcrumb   🔍 V3 ☀ ⚙       │
//   │  ├──────────────────────────────────────────┤
//   │  │                                          │
//   │ □│  ┌──────────┐ ┌──────────────────────┐  │
//   │ □│  │ tile     │ │ tile (wide)          │  │
//   │ □│  └──────────┘ └──────────────────────┘  │
//   │ □│  ┌──────────────────────────────────┐    │
//   │ □│  │ tile (full width)                │    │
//   │  │  └──────────────────────────────────┘    │
//   │  │                                          │
//   │ ⏻│                                          │
//   └──┴──────────────────────────────────────────┘
//
// Left "rail" is an icon-only column (64px wide on desktop, hidden on mobile
// behind a drawer). The page area is full width with a slim breadcrumb +
// controls strip at the top. Pages aren't restructured here — Panels render
// inside and get bento-tile styling via the [data-version="v3"] CSS layer.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { LogOut, Menu, Sun, Moon, X, MoreHorizontal, Keyboard, Monitor, Smartphone, BookOpen } from "lucide-react";
import { loadState, logout } from "../api";
import { nav, navItemVisible, DESKTOP_SECTIONS } from "../nav";
import Module from "./Module";
import WelcomeOverlay from "./WelcomeOverlay";
import ProfileDropdown from "./ProfileDropdown";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import { useAutoReload } from "./useAutoReload";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

export default function ShellV3({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const unreadMessages = useUnreadMessages({ active, onNavigate: setActive });
  const [error, setError]     = useState("");
  function navigate(key, arg = null) {
    setActive(key);
    setNavArg(arg);
  }

  // Deep links — a tapped push notification or an incoming gabruz://
  // URL dispatches a window event with { view, arg }. Mirrors the
  // handler in ShellV4; both shells can be active depending on viewport.
  useEffect(() => {
    let unsub = () => {};
    import("../deepLinks").then(({ onDeepLink }) => {
      unsub = onDeepLink(({ view, arg }) => {
        if (view) navigate(view, arg);
      });
    }).catch(() => {});
    return () => unsub();
  }, []);
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();

  const [showWelcome, setShowWelcome] = useState(
    () => sessionStorage.getItem("gabruz-just-logged-in") === "1"
  );
  useEffect(() => {
    if (showWelcome) sessionStorage.removeItem("gabruz-just-logged-in");
  }, [showWelcome]);

  // Profile quick-edit dropdown — anchored under the user chip in the
  // top-right of the topbar. Closes on outside-click / Escape.
  const [profileOpen, setProfileOpen] = useState(false);
  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (!e.target.closest?.(".appV3-meWrap")) setProfileOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setProfileOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  // Overflow menu — collapses theme toggle, keyboard shortcut hint and
  // (for admins) version switch into a single "…" so the topbar stops
  // reading as a control panel. Closes on outside-click / Escape.
  const [overflowOpen, setOverflowOpen] = useState(false);
  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e) => {
      if (!e.target.closest?.(".topbarOverflow")) setOverflowOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOverflowOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [overflowOpen]);
  const canPreviewVersion = (user.roles || []).some(r =>
    ["ADMIN","PRESIDENT","VICE_PRESIDENT"].includes(r)
  );

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  // Cross-device sync — see useAutoReload for details.
  useAutoReload(reload);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [reload]);

  // V3 uses the modern mobile-first component set for the main views
  // (Schedule / Squads / etc.), so it also honours the `desktopOnly`
  // nav flag — Availability is a legacy admin-planning matrix that
  // shouldn't clutter the V3 nav; admins triage RSVPs inline on
  // Schedule cards instead.
  const allowedLeaves = useMemo(
    () => nav.filter(n => navItemVisible(user, n, state.teams)),
    [user, state.teams]
  );

  // Build the 6-section desktop tree, dropping children the user
  // can't access and dropping any section left with 0 children.
  // Result: [{ ...section, childItems: [<full nav item>, ...] }]
  const allowedSections = useMemo(() => {
    const byKey = Object.fromEntries(allowedLeaves.map(n => [n.key, n]));
    return DESKTOP_SECTIONS
      .map(sec => {
        const childItems = sec.children.map(k => byKey[k]).filter(Boolean);
        if (childItems.length === 0) return null;
        return { ...sec, childItems };
      })
      .filter(Boolean);
  }, [allowedLeaves]);

  // Section that owns the currently-active leaf. Falls back to the
  // first accessible section if the current leaf is orphaned (e.g.
  // roles changed mid-session and stripped access).
  const activeSection = useMemo(() =>
    allowedSections.find(s => s.childItems.some(c => c.key === active))
      || allowedSections[0]
      || null,
  [allowedSections, active]);

  const activeItem = useMemo(() =>
    allowedLeaves.find(n => n.key === active),
  [allowedLeaves, active]);

  // Remember the last leaf visited per section, so returning to a
  // section restores the tab the user was on. Session-scoped so it
  // resets on browser close.
  const [lastLeafPerSection, setLastLeafPerSection] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("gabruz-last-leaf-per-section") || "{}");
    } catch { return {}; }
  });
  useEffect(() => {
    if (!activeSection) return;
    if (lastLeafPerSection[activeSection.key] === active) return;
    const next = { ...lastLeafPerSection, [activeSection.key]: active };
    setLastLeafPerSection(next);
    try { sessionStorage.setItem("gabruz-last-leaf-per-section", JSON.stringify(next)); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activeSection?.key]);

  function goToSection(sec) {
    // If the section is already open, no-op (avoids losing the
    // current tab position on double-click).
    if (activeSection?.key === sec.key) return;
    const remembered = lastLeafPerSection[sec.key];
    const target =
      (remembered && sec.childItems.some(c => c.key === remembered))
        ? remembered
        : sec.childItems[0].key;
    navigateTo(target);
  }

  function signOut() {
    logout();
    setUser(null);
  }
  function navigateTo(key) {
    setActive(key);
    setDrawerOpen(false);
  }

  const initial = (user.name || "?")[0]?.toUpperCase() || "?";

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
    <div className={`appV3${showWelcome ? " app--welcoming" : ""}`}>
      {showWelcome && <WelcomeOverlay onDone={() => setShowWelcome(false)} />}

      {/* ----- Left icon rail (desktop) ----- */}
      <aside className="appV3-rail">
        <button
          type="button"
          className="appV3-rail-brand"
          onClick={() => navigateTo("dashboard")}
          title="Gabruz Universe — home"
          aria-label="Go to dashboard"
        >
          <img src="/gabruz-brand.png" onError={e => e.currentTarget.style.display = "none"} alt="Gabruz Universe" />
        </button>
        <nav className="appV3-rail-nav" aria-label="Main">
          {allowedSections.map(sec => {
            const SecIcon = sec.icon;
            const isActive = activeSection?.key === sec.key;
            // Aggregate section-level badge — sum of any child badges.
            // Today only Messages carries one; add more here if needed.
            const secBadge = sec.childItems.some(c => c.key === "messages") && unreadMessages > 0
              ? unreadMessages : null;
            return (
              <button
                key={sec.key}
                type="button"
                className={isActive ? "appV3-rail-btn appV3-rail-btn--active" : "appV3-rail-btn"}
                onClick={() => goToSection(sec)}
                title={sec.label}
                aria-label={sec.label}
              >
                <span className="appV3-rail-iconWrap">
                  <SecIcon size={18} />
                  {secBadge && (
                    <span className="appV3-rail-badge">{secBadge > 99 ? "99+" : secBadge}</span>
                  )}
                </span>
                <span className="appV3-rail-label">{sec.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="appV3-rail-btn appV3-rail-out" onClick={signOut} title="Sign out" aria-label="Sign out">
          <LogOut size={18} />
          <span className="appV3-rail-label">Sign Out</span>
        </button>
      </aside>

      {/* ----- Right column: top strip + bento page ----- */}
      <div className="appV3-content">
        <header className="appV3-top">
          <button className="appV3-burger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <Menu size={18} />
          </button>
          <NotificationBell notifications={state.notifications} onNavigate={navigateTo}/>
          <div className="appV3-crumb">
            <span className="appV3-crumb-trail">
              Gabruz Universe
              {activeSection ? <> · {activeSection.label}</> : null}
              {" ·"}
            </span>
            <span className="appV3-crumb-active">{activeItem?.label || activeSection?.label || "Home"}</span>
          </div>

          <TeamSwitcher />

          {/* Global search intentionally omitted — the topbar previously
              rendered a dead <input placeholder="Search…" aria-label="Search…"/> with no
              handler wired. Removed until a real cross-view command
              palette lands. */}

          <div className="topbarOverflow">
            <button
              type="button"
              className="themeToggle"
              onClick={() => setOverflowOpen(o => !o)}
              title="More options"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
            >
              <MoreHorizontal size={16}/>
            </button>
            {overflowOpen && (
              <div className="topbarOverflow-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="topbarOverflow-item"
                  onClick={() => { setOverflowOpen(false); navigateTo("user-guide"); }}
                >
                  <BookOpen size={14}/>
                  <span>User Guide</span>
                </button>
                <div className="topbarOverflow-sep" role="separator"/>
                <button
                  type="button"
                  role="menuitem"
                  className="topbarOverflow-item"
                  onClick={() => { toggleTheme(); setOverflowOpen(false); }}
                >
                  {theme === "light" ? <Moon size={14}/> : <Sun size={14}/>}
                  <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="topbarOverflow-item"
                  onClick={() => {
                    setOverflowOpen(false);
                    window.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }));
                  }}
                >
                  <Keyboard size={14}/>
                  <span>Keyboard shortcuts</span>
                </button>
                {canPreviewVersion && (
                  <>
                    <div className="topbarOverflow-sep" role="separator"/>
                    <div className="topbarOverflow-label">Preview shell</div>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={version === "v3"}
                      className={`topbarOverflow-item${version === "v3" ? " topbarOverflow-item--active" : ""}`}
                      onClick={() => { setVersion("v3"); setOverflowOpen(false); }}
                    >
                      <Monitor size={14}/>
                      <span>Desktop</span>
                    </button>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={version === "v4"}
                      className={`topbarOverflow-item${version === "v4" ? " topbarOverflow-item--active" : ""}`}
                      onClick={() => { setVersion("v4"); setOverflowOpen(false); }}
                    >
                      <Smartphone size={14}/>
                      <span>Mobile</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="appV3-meWrap">
            <button
              type="button"
              className={`appV3-me${profileOpen ? " appV3-me--open" : ""}`}
              title="Your profile"
              onClick={() => setProfileOpen(o => !o)}
              aria-haspopup="dialog"
              aria-expanded={profileOpen}
            >
              <div className="appV3-me-avatar">{user.avatar || initial}</div>
              <div className="appV3-me-text">
                <strong>{user.name}</strong>
                <span>{user.roles[0].replace("_", " ")}</span>
              </div>
            </button>
            {profileOpen && (
              <ProfileDropdown
                user={user}
                setUser={setUser}
                state={state}
                reload={reload}
                onSignOut={signOut}
                onOpenFullProfile={() => navigateTo("profile")}
                onOpenUserGuide={() => navigateTo("user-guide")}
                onClose={() => setProfileOpen(false)}
              />
            )}
          </div>
        </header>

        {/* ── Section tab strip — only when the active section has >1 child ── */}
        {activeSection && activeSection.childItems.length > 1 && (
          <div className="appV3-secTabs" role="tablist" aria-label={`${activeSection.label} sections`}>
            {activeSection.childItems.map(child => {
              const ChildIcon = child.icon;
              const isOn = active === child.key;
              const badge = child.key === "messages" && unreadMessages > 0
                ? unreadMessages : null;
              return (
                <button
                  key={child.key}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  className={isOn ? "appV3-secTab appV3-secTab--active" : "appV3-secTab"}
                  onClick={() => navigateTo(child.key)}
                >
                  <ChildIcon size={14}/>
                  <span>{child.label}</span>
                  {badge && <em className="appV3-secTab-badge">{badge > 99 ? "99+" : badge}</em>}
                </button>
              );
            })}
          </div>
        )}

        <main className="appV3-main">
          <div className="page page--v3">
            {loading ? (
              <div className="empty">
                <h2>Loading…</h2>
                <p>Fetching data from the API.</p>
              </div>
            ) : error ? (
              <div className="empty">
                <h2>Couldn't load data</h2>
                <p>{error}</p>
                <button className="primary" onClick={reload}>Retry</button>
              </div>
            ) : (
              <Module active={active} setActive={navigate} navArg={navArg} state={state} reload={reload} user={user} setUser={setUser} />
            )}
          </div>
        </main>
      </div>

      {/* ----- Mobile slide-in drawer ----- */}
      {drawerOpen && (
        <>
          <div className="appV3-drawer-veil" onClick={() => setDrawerOpen(false)} />
          <aside className="appV3-drawer">
            <div className="appV3-drawer-head">
              <strong>Navigation</strong>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"><X size={18}/></button>
            </div>
            {allowedSections.map(sec => {
              const SecIcon = sec.icon;
              // Single-child sections render as one flat row; multi-child
              // sections render as a header + indented children.
              if (sec.childItems.length === 1) {
                const only = sec.childItems[0];
                const OnlyIcon = only.icon;
                return (
                  <button
                    key={sec.key}
                    type="button"
                    className={active === only.key ? "appV3-drawer-link appV3-drawer-link--active" : "appV3-drawer-link"}
                    onClick={() => navigateTo(only.key)}
                  >
                    <OnlyIcon size={16}/><span>{sec.label}</span>
                  </button>
                );
              }
              return (
                <div key={sec.key} className="appV3-drawer-group">
                  <div className="appV3-drawer-groupHead">
                    <SecIcon size={13}/><span>{sec.label}</span>
                  </div>
                  {sec.childItems.map(child => {
                    const ChildIcon = child.icon;
                    return (
                      <button
                        key={child.key}
                        type="button"
                        className={active === child.key
                          ? "appV3-drawer-link appV3-drawer-link--child appV3-drawer-link--active"
                          : "appV3-drawer-link appV3-drawer-link--child"}
                        onClick={() => navigateTo(child.key)}
                      >
                        <ChildIcon size={14}/><span>{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
            <button className="appV3-drawer-link appV3-drawer-out" onClick={signOut}>
              <LogOut size={16} /> Sign Out
            </button>
          </aside>
        </>
      )}
      <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />
    </div>
    </ActiveTeamProvider>
  );
}
